import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CodeSearchResponse } from '@mtna/classification-service-core-ui';
import { MtnaLogger } from '@mtna/lib-ui';
import {
  Catalog,
  Classification,
  ClassificationSummary,
  Code,
  DataProduct,
  DataSource,
  Variable,
  VariableSummary,
} from '@mtna/pojo-consumer-ui';
import { CodeOptions } from '@mtna/rds-base-ui';

import { serializeVariableSummariesParameters, VariableSummariesParameters } from '../models';
import { RdsFormattedVariableProfile } from '../models/formatted-variable-profile.model';
import { RdsProfileParameters, serializeProfileParams } from '../models/profile-parameters.model';
import { RdsApiService } from './rds-api.service';

/**
 * Represents
 * <https://bitbucket.org/mtnaus/rds-metadata/src/develop/src/main/java/us/mtna/rds/metadata/ws/MetadataController.java>
 */
@Injectable({
  providedIn: 'root',
})
export class RdsMetadataService {
  /** base url for the service */
  protected get CATALOG_URL(): string {
    return `${this.apiService.getApiUrl()}/catalog`;
  }

  /** url with ID of the selected catalog */
  protected get CATALOG_ID_URL(): string {
    return `${this.CATALOG_URL}/${this.apiService.getCatalogId()}`;
  }

  /** url with ID of the selected data product */
  protected get DATA_PRODUCT_URL(): string {
    return `${this.CATALOG_ID_URL}/${this.apiService.getDataProductId()}`;
  }

  protected _rootCatalogCache$ = new ReplaySubject<Catalog>(1);
  protected _rootCatalogRequest$: Observable<Catalog>;
  protected _catalogCache = new Map<string, Catalog>();
  protected _dataProductCache = new Map<string, DataProduct>();
  protected _dataSourcesCache = new Map<string, DataSource[]>();
  protected _classificationSummaryCache = new Map<string, ClassificationSummary[]>();
  protected _classificationCache = new Map<string, Classification>();
  protected _codesCache = new Map<string, Code[]>();
  protected _codeSearchCache = new Map<string, CodeSearchResponse>();
  protected _variableSummaryCache = new Map<string, VariableSummary[]>();
  protected _variableCountCache = new Map<string, number>();
  protected _variableCache = new Map<string, Variable>();
  protected _frequenciesCache = new Map<string, { sets: Array<any> }>();
  protected _variableProfilesCache = new Map<string, { [id: string]: RdsFormattedVariableProfile }>();

  constructor(protected apiService: RdsApiService, protected http: HttpClient, protected logger: MtnaLogger) {}

  /**
   * Get the root catalog of the system. This will hold a list of all the catalogs and data products that are available on this server.
   * This would ideally be used to power an entry point into an application. The catalog provides a starting point for users to view what
   * is available to them and drill down into a catalog or data product of their interest. The catalogs and products may have descriptive
   * metadata on them that is useful to display.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns the root catalog
   */
  getRootCatalog(refresh: boolean = false): Observable<Catalog> {
    if (refresh || !this._rootCatalogRequest$) {
      this.logger.debug(`[RdsMetadataService] getRootCatalog - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._rootCatalogRequest$ = this.http.get<Catalog>(this.CATALOG_URL);
      this._rootCatalogRequest$.subscribe(
        (result) => this._rootCatalogCache$.next(result),
        (err) => this._rootCatalogCache$.error(err)
      );
    }
    return this._rootCatalogCache$.asObservable();
  }

  /**
   * Get catalog. Returns the specified catalog. This allows a more specific view of a catalog and its data products,
   * it is a subset of the root catalog and holds no additional information to what can be found in the broader get catalog endpoint.
   * @remarks This method uses caching, returns a cold observable.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the `RdsApiService`'s selected catalog
   */
  getCatalog(refresh: boolean = false): Observable<Catalog> {
    const mapKey: string = this._getCatalogDataProductMapKey();
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getCatalog - Refresh requested, removing ${mapKey} from cache`);
      this._catalogCache.delete(mapKey);
    }
    if (this._catalogCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getCatalog - Returning ${mapKey} from cache`);
      return of(this._catalogCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getCatalog - Fetching, ${mapKey} not in cache`);
    return this.http.get<Catalog>(`${this.CATALOG_ID_URL}`).pipe(tap((catalog) => this._catalogCache.set(mapKey, catalog)));
  }

  /**
   * Get data product. Returns the specified data product. This shows just a single data product in a catalog,
   * there is no additional information in this call, it simply limits the scope of what is returned.
   * @remarks This method uses caching, returns a cold observable.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the `RdsApiService`'s selected data product
   */
  getDataProduct(refresh: boolean = false): Observable<DataProduct> {
    const mapKey: string = this._getCatalogDataProductMapKey();
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getCatalog - Refresh requested, removing ${mapKey} from cache`);
      this._dataProductCache.delete(mapKey);
    }
    if (this._dataProductCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getCatalog - Returning ${mapKey} from cache`);
      return of(this._dataProductCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getCatalog - Fetching, ${mapKey} not in cache`);
    return this.http
      .get<DataProduct>(`${this.DATA_PRODUCT_URL}`)
      .pipe(tap((dataProduct) => this._dataProductCache.set(mapKey, dataProduct)));
  }

  /**
   * Get data sources. Gets the specified data products data sources. This is an administrative end point for
   * viewing data source connection information. This information is typically hidden from the general user
   * however,for the administrator it allows them to see connection information that they may need to edit
   * if there are changes to their database.
   * @remarks This method uses caching, returns a cold observable.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns data sources for the `RdsApiService`'s selected data product
   */
  getDataSources(refresh: boolean = false): Observable<DataSource[]> {
    const mapKey: string = this._getCatalogDataProductMapKey();
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getDataSources - Refresh requested, removing ${mapKey} from cache`);
      this._dataSourcesCache.delete(mapKey);
    }
    if (this._dataSourcesCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getDataSources - Returning ${mapKey} from cache`);
      return of(this._dataSourcesCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getDataSources - Fetching, ${mapKey} not in cache`);
    return this.http
      .get<DataSource[]>(`${this.DATA_PRODUCT_URL}/dataSources`)
      .pipe(tap((dataSources) => this._dataSourcesCache.set(mapKey, dataSources)));
  }

  /**
   * Get classifications. Get the classifications of a specific data product.
   * Returns a list of summary objects about any classifications that are used in the specified data product.
   * These summary objects hold the classification uri and id (among other things)
   * either of which can be used to get more information about the classification.
   * @remarks This method uses caching, returns a cold observable.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns classification summaries for the `RdsApiService`'s selected data product
   */
  getClassifications(refresh: boolean = false): Observable<ClassificationSummary[]> {
    const mapKey: string = this._getCatalogDataProductMapKey();
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getClassifications - Refresh requested, removing ${mapKey} from cache`);
      this._classificationSummaryCache.delete(mapKey);
    }
    if (this._classificationSummaryCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getClassifications - Returning ${mapKey} from cache`);
      return of(this._classificationSummaryCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getClassifications - Fetching, ${mapKey} not in cache`);
    return this.http
      .get<ClassificationSummary[]>(`${this.DATA_PRODUCT_URL}/classifications`)
      .pipe(tap((classifications) => this._classificationSummaryCache.set(mapKey, classifications)));
  }

  /**
   * Get classification. Returns the specified classification with more detail than the summary.
   * Codes will be excluded from this object, the idea being that this classification could have
   * a large amount of codes and clients can build these codes up through the used of the codes endpoints.
   * @remarks This method uses caching, returns a cold observable.
   * @privateRemarks In the future, we should support caching classifications to be used across data products.
   * @param classificationId The ID or URI the classification to return.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the specified classification for the `RdsApiService`'s selected data product
   */
  getClassification(classificationId: string, refresh: boolean = false): Observable<Classification> {
    const mapKey = `${this._getCatalogDataProductMapKey()}.${classificationId}`;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getClassification - Refresh requested, removing ${mapKey} from cache`);
      this._classificationCache.delete(mapKey);
    }
    if (this._classificationCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getClassification - Returning ${mapKey} from cache`);
      return of(this._classificationCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getClassification - Fetching, ${mapKey} not in cache`);
    return this.http
      .get<Classification>(`${this.DATA_PRODUCT_URL}/classification/${classificationId}`)
      .pipe(tap((classification) => this._classificationCache.set(mapKey, classification)));
  }

  /**
   * Get codes. This allows the client to page through or build up the codes of the classification as desired.
   * @remarks This method uses caching, returns a cold observable.
   * @param classificationId The ID or URI the classification to return.
   * @param options extra options for filtering/sorting/paging
   * @param includeParents whether parent URI should be included on the code (if they are available). Defaults to false.
   * @param includeChildren whether child codes should be included on the code (if they are available). Defaults to false.
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the specified codes within the specified classification for the `RdsApiService`'s selected data product
   */
  getCodes(
    classificationId: string,
    options?: CodeOptions,
    includeParents: boolean = false,
    includeChildren: boolean = false,
    refresh: boolean = false
  ): Observable<Code[]> {
    let path = `${this.DATA_PRODUCT_URL}/classification/${classificationId}/codes?`;
    if (!!options) {
      if (options.sortOrder !== null && options.sortOrder !== undefined) {
        path += `sortOrder=${options.sortOrder}&`;
      }
      if (options.limit !== null && options.limit !== undefined) {
        path += `limit=${options.limit}&`;
      }
      if (options.offset !== null && options.offset !== undefined) {
        path += `offset=${options.offset}&`;
      }
      if (options.ids !== null && options.ids !== undefined && options.ids.length) {
        path += `ids=${options.ids.join(',')}&`;
      }
      if (options.names !== null && options.names !== undefined && options.names.length) {
        path += `names=${options.names.join(',')}&`;
      }
      if (includeParents) {
        path += `includeParents=${includeParents}&`;
      }
      if (includeChildren) {
        path += `includeChildren=${includeChildren}&`;
      }
      if (path[path.length - 1] === '&') {
        path = path.substr(0, path.length - 1);
      }
    }

    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getCodes - Refresh requested, removing ${mapKey} from cache`);
      this._codesCache.delete(mapKey);
    }
    if (this._codesCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getCodes - Returning ${mapKey} from cache`);
      return of(this._codesCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getCodes - Fetching, ${mapKey} not in cache`);
    return this.http.get<Code[]>(path).pipe(tap((codes) => this._codesCache.set(mapKey, codes)));
  }

  /**
   * Search for codes within a given classification.
   * @remarks This method uses caching, returns a cold observable.
   * @param classificationId The ID or URI of the classification that contains the codes.
   * @param searchText The text to search on (optional). Will search for this text within a code's value and/or name
   * @param offset The starting index of the codes
   * @param limit The max number of codes to return
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the Codes that were found based on the search text.
   */
  searchCodes(
    classificationId: string,
    searchText?: string,
    offset: number = 0,
    limit: number = 25,
    refresh: boolean = false
  ): Observable<CodeSearchResponse> {
    let path = `${this.DATA_PRODUCT_URL}/classification/${classificationId}/codes/search?`;
    if (offset !== null && offset !== undefined) {
      path += `offset=${offset}&`;
    }
    if (limit !== null && limit !== undefined) {
      path += `limit=${limit}&`;
    }
    if (searchText !== null && searchText !== undefined) {
      path += `searchText=${searchText}&`;
    }
    // Remove trailing ampersand
    if (path[path.length - 1] === '&') {
      path = path.substr(0, path.length - 1);
    }

    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] searchCodes - Refresh requested, removing ${mapKey} from cache`);
      this._codeSearchCache.delete(mapKey);
    }
    if (this._codeSearchCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] searchCodes - Returning ${mapKey} from cache`);
      return of(this._codeSearchCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] searchCodes - Fetching, ${mapKey} not in cache`);
    return this.http.get<CodeSearchResponse>(path).pipe(tap((codes) => this._codeSearchCache.set(mapKey, codes)));
  }

  /**
   * Get variable summaries. Get the variables of a specific data product.
   * Returns a list of summary objects about any variables that are used in the specified data product.
   * These summaries contain both the variable URI and ID, which can be used to get more detailed information about the variable.
   * @remarks This method uses caching, returns a cold observable.
   * @param params optional parameters
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the variable summaries for the `RdsApiService`'s selected data product
   */
  getVariableSummaries(params?: VariableSummariesParameters, refresh: boolean = false): Observable<VariableSummary[]> {
    const path = `${this.DATA_PRODUCT_URL}/variables?${serializeVariableSummariesParameters(params)}`;
    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getVariableSummaries - Refresh requested, removing ${mapKey} from cache`);
      this._variableSummaryCache.delete(mapKey);
    }
    if (this._variableSummaryCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getVariableSummaries - Returning ${mapKey} from cache`);
      return of(this._variableSummaryCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getVariableSummaries - Fetching, ${mapKey} not in cache`);
    return this.http.get<VariableSummary[]>(path).pipe(tap((variables) => this._variableSummaryCache.set(mapKey, variables)));
  }

  /**
   * Get total number of variable summaries for a query.
   * @remarks This method uses caching, returns a cold observable.
   * @param params optional parameters
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the number of variable summaries for the query
   */
  getVariableSummariesCount(params?: VariableSummariesParameters, refresh: boolean = false): Observable<number> {
    const path = `${this.DATA_PRODUCT_URL}/variables/count?${serializeVariableSummariesParameters(params)}`;
    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getVariablesCount - Refresh requested, removing ${mapKey} from cache`);
      this._variableCountCache.delete(mapKey);
    }
    if (this._variableCountCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getVariablesCount - Returning ${mapKey} from cache`);
      return of(this._variableCountCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getVariablesCount - Fetching, ${mapKey} not in cache`);
    return this.http.get<number>(path).pipe(tap((count) => this._variableCountCache.set(mapKey, count)));
  }

  /**
   * Get variable. Returns a variable with more detail than what is provided by the summary object.
   * If the variable has a classification, its URI should be available on the variable which can be used to retrieve the classification.
   * @remarks This method uses caching, returns a cold observable.
   * @privateRemarks In the future, we should support caching variables to be used across data products.
   * @param variableId The Id or URI of the variable that is desired
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the specified variable for the `RdsApiService`'s selected data product
   */
  getVariable(variableId: string, refresh: boolean = false): Observable<Variable> {
    const mapKey = `${this._getCatalogDataProductMapKey()}.${variableId}`;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getVariable - Refresh requested, removing ${mapKey} from cache`);
      this._variableCache.delete(mapKey);
    }
    if (this._variableCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getVariable - Returning ${mapKey} from cache`);
      return of(this._variableCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getVariable - Fetching, ${mapKey} not in cache`);
    return this.http
      .get<Variable>(`${this.DATA_PRODUCT_URL}/variable/${variableId}`)
      .pipe(tap((variable) => this._variableCache.set(mapKey, variable)));
  }

  /** Get the frequencies for a specified variable of a data product */
  /**
   * Get frequencies. Get the frequencies for a specified variable of a data product.
   * This method is primarily available to get the frequencies in a format that is
   * consumable by the supported charting serializations. If there are no frequencies available the call will return null.
   * @remarks This method uses caching, returns a cold observable.
   * @param variableId The Id or URI of the variable that contains the desired frequencies.
   * @param inject Whether to inject code values
   * @param format Response format
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns the frequencies for a specified variable for the `RdsApiService`'s selected data product
   */
  getVariableFrequency(
    variableId: string,
    inject: boolean = false,
    format?: string,
    refresh: boolean = false
  ): Observable<{ sets: Array<any> }> {
    let path = `${this.DATA_PRODUCT_URL}/variable/${variableId}/frequencies?inject=${inject}`;
    if (!!format) {
      path += `&format=${format}`;
    }

    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getVariableFrequency - Refresh requested, removing ${mapKey} from cache`);
      this._frequenciesCache.delete(mapKey);
    }
    if (this._frequenciesCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getVariableFrequency - Returning ${mapKey} from cache`);
      return of(this._frequenciesCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getVariableFrequency - Fetching, ${mapKey} not in cache`);
    return this.http.get<{ sets: Array<any> }>(path).pipe(tap((frequencies) => this._frequenciesCache.set(mapKey, frequencies)));
  }

  getVariableProfiles(params: RdsProfileParameters, refresh: boolean = false): Observable<{ [id: string]: RdsFormattedVariableProfile }> {
    const path = `${this.DATA_PRODUCT_URL}/variables/profile?${serializeProfileParams(params)}`;

    const mapKey = path;
    if (refresh) {
      this.logger.debug(`[RdsMetadataService] getVariableProfiles - Refresh requested, removing ${mapKey} from cache`);
      this._variableProfilesCache.delete(mapKey);
    }
    if (this._variableProfilesCache.has(mapKey)) {
      this.logger.debug(`[RdsMetadataService] getVariableProfiles - Returning ${mapKey} from cache`);
      return of(this._variableProfilesCache.get(mapKey));
    }
    this.logger.debug(`[RdsMetadataService] getVariableProfiles - Fetching, ${mapKey} not in cache`);
    return this.http
      .get<{ [id: string]: RdsFormattedVariableProfile }>(path)
      .pipe(tap((vp) => this._variableProfilesCache.set(mapKey, vp)));
  }

  /**
   * Combines the ID of the `RdsApiService`'s selected catalog and data product to be used as a cache map key.
   * @returns map key for a specific catalog and data product
   */
  private _getCatalogDataProductMapKey(): string {
    return `${this.apiService.getCatalogId()}.${this.apiService.getDataProductId()}`;
  }
}
